'use client';

import { useTranslations } from 'next-intl';
import {
  GAME_STATUS_KEYS,
  ACTION_KEYS,
  LOADING_KEYS,
  COLLECTION_KEYS,
  JOURNAL_KEYS,
  EDITOR_KEYS,
  UI_KEYS,
  GAME_DETAILS_KEYS,
  AUTH_KEYS,
  NAVIGATION_KEYS,
  TODO_KEYS,
  GAME_STATUS,
  type GameStatus,
} from '../i18n-constants';

// Custom hook for game status translations
export function useGameStatusTranslations() {
  const t = useTranslations();

  return {
    getStatusLabel: (status: GameStatus) => {
      switch (status) {
        case GAME_STATUS.BACKLOG:
          return t(GAME_STATUS_KEYS.BACKLOG);
        case GAME_STATUS.WISHLIST:
          return t(GAME_STATUS_KEYS.WISHLIST);
        case GAME_STATUS.COMPLETED:
          return t(GAME_STATUS_KEYS.COMPLETED);
        case GAME_STATUS.PLAYING:
          return t(GAME_STATUS_KEYS.PLAYING);
        default:
          return status;
      }
    },

    getStatusOptions: () => [
      { value: GAME_STATUS.BACKLOG, label: t(GAME_STATUS_KEYS.BACKLOG) },
      { value: GAME_STATUS.WISHLIST, label: t(GAME_STATUS_KEYS.WISHLIST) },
      { value: GAME_STATUS.PLAYING, label: t(GAME_STATUS_KEYS.PLAYING) },
      { value: GAME_STATUS.COMPLETED, label: t(GAME_STATUS_KEYS.COMPLETED) },
    ],
  };
}

// Custom hook for action translations
export function useActionTranslations() {
  const t = useTranslations();

  return {
    save: t(ACTION_KEYS.SAVE),
    cancel: t(ACTION_KEYS.CANCEL),
    delete: t(ACTION_KEYS.DELETE),
    edit: t(ACTION_KEYS.EDIT),
    create: t(ACTION_KEYS.CREATE),
    add: t(ACTION_KEYS.ADD),
    remove: t(ACTION_KEYS.REMOVE),
    update: t(ACTION_KEYS.UPDATE),
    submit: t(ACTION_KEYS.SUBMIT),
    confirm: t(ACTION_KEYS.CONFIRM),
  };
}

// Custom hook for loading state translations
export function useLoadingTranslations() {
  const t = useTranslations();

  return {
    loading: t(LOADING_KEYS.LOADING),
    saving: t(LOADING_KEYS.SAVING),
    adding: t(LOADING_KEYS.ADDING),
    updating: t(LOADING_KEYS.UPDATING),
    deleting: t(LOADING_KEYS.DELETING),
    creating: t(LOADING_KEYS.CREATING),
    removing: t(LOADING_KEYS.REMOVING),
  };
}

// Custom hook for collection translations
export function useCollectionTranslations() {
  const t = useTranslations();

  return {
    addToCollection: t(COLLECTION_KEYS.ADD_TO_COLLECTION),
    removeFromCollection: t(COLLECTION_KEYS.REMOVE_FROM_COLLECTION),
    updateCollection: t(COLLECTION_KEYS.UPDATE_COLLECTION),
    loadingCollection: t(COLLECTION_KEYS.LOADING_COLLECTION),
    failedToLoadCollection: t(COLLECTION_KEYS.FAILED_TO_LOAD_COLLECTION),
    failedToAddToCollection: t(COLLECTION_KEYS.FAILED_TO_ADD_TO_COLLECTION),
    failedToUpdateStatus: t(COLLECTION_KEYS.FAILED_TO_UPDATE_STATUS),
    failedToRemoveFromCollection: t(
      COLLECTION_KEYS.FAILED_TO_REMOVE_FROM_COLLECTION,
    ),
    myGameCollection: t(COLLECTION_KEYS.MY_GAME_COLLECTION),
    allGames: t(COLLECTION_KEYS.ALL_GAMES),
    noGamesFound: t(COLLECTION_KEYS.NO_GAMES_FOUND),
    startBuilding: t(COLLECTION_KEYS.START_BUILDING),
    changeGameStatus: t(COLLECTION_KEYS.CHANGE_GAME_STATUS),
    removeFromCollectionTooltip: t(
      COLLECTION_KEYS.REMOVE_FROM_COLLECTION_TOOLTIP,
    ),
    developer: t(COLLECTION_KEYS.DEVELOPER),
    loadMoreGames: t(COLLECTION_KEYS.LOAD_MORE_GAMES),
    confirmRemoveTitle: t(COLLECTION_KEYS.CONFIRM_REMOVE_TITLE),
    confirmRemoveMessage: t(COLLECTION_KEYS.CONFIRM_REMOVE_MESSAGE),
    confirmRemoveGameName: t(COLLECTION_KEYS.CONFIRM_REMOVE_GAME_NAME),
    typeToConfirm: t(COLLECTION_KEYS.TYPE_TO_CONFIRM),
    typeToConfirmPlaceholder: t(COLLECTION_KEYS.TYPE_TO_CONFIRM_PLACEHOLDER),

    // Functions with parameters
    noGamesWithStatus: (status: string) =>
      t(COLLECTION_KEYS.NO_GAMES_WITH_STATUS, { status }),
    gameCount: (count: number, status: string) =>
      t(COLLECTION_KEYS.GAME_COUNT, {
        count,
        status,
        plural: count !== 1 ? 's' : '',
      }),
    gameCountSimple: (count: number) =>
      t(COLLECTION_KEYS.GAME_COUNT_SIMPLE, {
        count,
        plural: count !== 1 ? 's' : '',
      }),
    addedOn: (date: string) => t(COLLECTION_KEYS.ADDED_ON, { date }),
    platform: (platform: string) => t(COLLECTION_KEYS.PLATFORM, { platform }),
  };
}

// Custom hook for journal translations
export function useJournalTranslations() {
  const t = useTranslations();

  return {
    createEntry: t(JOURNAL_KEYS.CREATE_ENTRY),
    saveEntry: t(JOURNAL_KEYS.SAVE_ENTRY),
    editEntry: t(JOURNAL_KEYS.EDIT_ENTRY),
    deleteEntry: t(JOURNAL_KEYS.DELETE_ENTRY),
    cancelEdit: t(JOURNAL_KEYS.CANCEL_EDIT),
    loadingEntries: t(JOURNAL_KEYS.LOADING_ENTRIES),
    noEntries: t(JOURNAL_KEYS.NO_ENTRIES),
    failedToLoadEntries: t(JOURNAL_KEYS.FAILED_TO_LOAD_ENTRIES),
    failedToSaveEntry: t(JOURNAL_KEYS.FAILED_TO_SAVE_ENTRY),
    failedToDeleteEntry: t(JOURNAL_KEYS.FAILED_TO_DELETE_ENTRY),
    entryTitle: t(JOURNAL_KEYS.ENTRY_TITLE),
    content: t(JOURNAL_KEYS.CONTENT),
    chooseGame: t(JOURNAL_KEYS.CHOOSE_GAME),
    game: t(JOURNAL_KEYS.GAME),
    contentPlaceholder: t(JOURNAL_KEYS.CONTENT_PLACEHOLDER),
  };
}

// Custom hook for rich text editor translations
export function useEditorTranslations() {
  const t = useTranslations();

  return {
    placeholder: t(EDITOR_KEYS.PLACEHOLDER),
    toolbarLabel: t(EDITOR_KEYS.TOOLBAR_LABEL),
    blockType: t(EDITOR_KEYS.BLOCK_TYPE),
    paragraph: t(EDITOR_KEYS.PARAGRAPH),
    heading1: t(EDITOR_KEYS.HEADING_1),
    heading2: t(EDITOR_KEYS.HEADING_2),
    heading3: t(EDITOR_KEYS.HEADING_3),
    heading4: t(EDITOR_KEYS.HEADING_4),
    heading5: t(EDITOR_KEYS.HEADING_5),
    heading6: t(EDITOR_KEYS.HEADING_6),
    quote: t(EDITOR_KEYS.QUOTE),
    codeBlock: t(EDITOR_KEYS.CODE_BLOCK),
    bold: t(EDITOR_KEYS.BOLD),
    italic: t(EDITOR_KEYS.ITALIC),
    underline: t(EDITOR_KEYS.UNDERLINE),
    strikethrough: t(EDITOR_KEYS.STRIKETHROUGH),
    inlineCode: t(EDITOR_KEYS.INLINE_CODE),
    link: t(EDITOR_KEYS.LINK),
    editLink: t(EDITOR_KEYS.EDIT_LINK),
    removeLink: t(EDITOR_KEYS.REMOVE_LINK),
    openLink: t(EDITOR_KEYS.OPEN_LINK),
    linkUrl: t(EDITOR_KEYS.LINK_URL),
    linkPlaceholder: t(EDITOR_KEYS.LINK_PLACEHOLDER),
    applyLink: t(EDITOR_KEYS.APPLY_LINK),
    invalidLink: t(EDITOR_KEYS.INVALID_LINK),
    bulletList: t(EDITOR_KEYS.BULLET_LIST),
    orderedList: t(EDITOR_KEYS.ORDERED_LIST),
    taskList: t(EDITOR_KEYS.TASK_LIST),
    insertTable: t(EDITOR_KEYS.INSERT_TABLE),
    table: t(EDITOR_KEYS.TABLE),
    addRowAfter: t(EDITOR_KEYS.ADD_ROW_AFTER),
    addColumnAfter: t(EDITOR_KEYS.ADD_COLUMN_AFTER),
    deleteRow: t(EDITOR_KEYS.DELETE_ROW),
    deleteColumn: t(EDITOR_KEYS.DELETE_COLUMN),
    toggleHeaderRow: t(EDITOR_KEYS.TOGGLE_HEADER_ROW),
    deleteTable: t(EDITOR_KEYS.DELETE_TABLE),
    horizontalRule: t(EDITOR_KEYS.HORIZONTAL_RULE),
    undo: t(EDITOR_KEYS.UNDO),
    redo: t(EDITOR_KEYS.REDO),
    slashMenuLabel: t(EDITOR_KEYS.SLASH_MENU_LABEL),
    slashNoResults: t(EDITOR_KEYS.SLASH_NO_RESULTS),
    tooLong: t(EDITOR_KEYS.TOO_LONG),
  };
}

export type EditorTranslations = ReturnType<typeof useEditorTranslations>;

// Custom hook for UI translations
export function useUITranslations() {
  const t = useTranslations();

  return {
    tryAgain: t(UI_KEYS.TRY_AGAIN),
    loadMore: t(UI_KEYS.LOAD_MORE),
    noResults: t(UI_KEYS.NO_RESULTS),
    searchPlaceholder: t(UI_KEYS.SEARCH_PLACEHOLDER),
    emptyState: t(UI_KEYS.EMPTY_STATE),
    unexpectedError: t(UI_KEYS.UNEXPECTED_ERROR),
  };
}

// Custom hook for game details translations
export function useGameDetailsTranslations() {
  const t = useTranslations();

  return {
    loadingGameDetails: t(GAME_DETAILS_KEYS.LOADING_GAME_DETAILS),
    gameNotFound: t(GAME_DETAILS_KEYS.GAME_NOT_FOUND),
    gameNotFoundMessage: t(GAME_DETAILS_KEYS.GAME_NOT_FOUND_MESSAGE),
    status: t(GAME_DETAILS_KEYS.STATUS),
    changeGameStatus: t(GAME_DETAILS_KEYS.CHANGE_GAME_STATUS),
    platform: t(GAME_DETAILS_KEYS.PLATFORM),
    developer: t(GAME_DETAILS_KEYS.DEVELOPER),
    released: t(GAME_DETAILS_KEYS.RELEASED),
    todos: t(GAME_DETAILS_KEYS.TODOS),
    journal: t(GAME_DETAILS_KEYS.JOURNAL),
  };
}

// Custom hook for auth page translations
export function useAuthTranslations() {
  const t = useTranslations();

  return {
    login: t(AUTH_KEYS.LOGIN),
    signup: t(AUTH_KEYS.SIGNUP),
    logout: t(AUTH_KEYS.LOGOUT),
    signIn: t(AUTH_KEYS.SIGN_IN),
    signUp: t(AUTH_KEYS.SIGN_UP),
    signOut: t(AUTH_KEYS.SIGN_OUT),
    loggingIn: t(AUTH_KEYS.LOGGING_IN),
    signingUp: t(AUTH_KEYS.SIGNING_UP),
    failedToLogin: t(AUTH_KEYS.FAILED_TO_LOGIN),
    failedToSignup: t(AUTH_KEYS.FAILED_TO_SIGNUP),
    appTitle: t(AUTH_KEYS.APP_TITLE),
    signInWithSteam: t(AUTH_KEYS.SIGN_IN_WITH_STEAM),
    or: t(AUTH_KEYS.OR),
    email: t(AUTH_KEYS.EMAIL),
    username: t(AUTH_KEYS.USERNAME),
    password: t(AUTH_KEYS.PASSWORD),
    confirmPassword: t(AUTH_KEYS.CONFIRM_PASSWORD),
    enterEmail: t(AUTH_KEYS.ENTER_EMAIL),
    enterUsername: t(AUTH_KEYS.ENTER_USERNAME),
    enterPassword: t(AUTH_KEYS.ENTER_PASSWORD),
    confirmPasswordPlaceholder: t(AUTH_KEYS.CONFIRM_PASSWORD_PLACEHOLDER),
    loginFailed: t(AUTH_KEYS.LOGIN_FAILED),
    creatingAccount: t(AUTH_KEYS.CREATING_ACCOUNT),
    registrationFailed: t(AUTH_KEYS.REGISTRATION_FAILED),
    passwordsDoNotMatch: t(AUTH_KEYS.PASSWORDS_DO_NOT_MATCH),
    dontHaveAccount: t(AUTH_KEYS.DONT_HAVE_ACCOUNT),
    alreadyHaveAccount: t(AUTH_KEYS.ALREADY_HAVE_ACCOUNT),
    joinGamersNotebook: t(AUTH_KEYS.JOIN_GAMERS_NOTEBOOK),
  };
}

// Custom hook for navigation translations
export function useNavigationTranslations() {
  const t = useTranslations();

  return {
    journal: t(NAVIGATION_KEYS.JOURNAL),
    belongsTo: t(NAVIGATION_KEYS.BELONGS_TO),
  };
}

export function useTodoTranslations() {
  const t = useTranslations();

  return {
    addItemPlaceholder: t(TODO_KEYS.ADD_ITEM_PLACEHOLDER),
    newListPlaceholder: t(TODO_KEYS.NEW_LIST_PLACEHOLDER),
    defaultListTitle: t(TODO_KEYS.DEFAULT_LIST_TITLE),
    emptyTitle: t(TODO_KEYS.EMPTY_TITLE),
    emptyHint: t(TODO_KEYS.EMPTY_HINT),
    done: t(TODO_KEYS.DONE),
    showDone: t(TODO_KEYS.SHOW_DONE),
    hideDone: t(TODO_KEYS.HIDE_DONE),
    notesPlaceholder: t(TODO_KEYS.NOTES_PLACEHOLDER),
    addNote: t(TODO_KEYS.ADD_NOTE),
    editItem: t(TODO_KEYS.EDIT_ITEM),
    deleteItem: t(TODO_KEYS.DELETE_ITEM),
    dragToReorder: t(TODO_KEYS.DRAG_TO_REORDER),
    markCompleted: t(TODO_KEYS.MARK_COMPLETED),
    markIncomplete: t(TODO_KEYS.MARK_INCOMPLETE),
    listOptions: t(TODO_KEYS.LIST_OPTIONS),
    renameList: t(TODO_KEYS.RENAME_LIST),
    clearCompleted: t(TODO_KEYS.CLEAR_COMPLETED),
    deleteList: t(TODO_KEYS.DELETE_LIST),
    confirmDeleteListTitle: t(TODO_KEYS.CONFIRM_DELETE_LIST_TITLE),
    confirmDeleteListMessage: (title: string, count: number) =>
      t(TODO_KEYS.CONFIRM_DELETE_LIST_MESSAGE, { title, count }),
    itemDeleted: t(TODO_KEYS.ITEM_DELETED),
    itemsCleared: (count: number) => t(TODO_KEYS.ITEMS_CLEARED, { count }),
    undo: t(TODO_KEYS.UNDO),
    saveFailed: t(TODO_KEYS.SAVE_FAILED),
    progress: (completed: number, total: number) =>
      t(TODO_KEYS.PROGRESS, { completed, total }),
    keyboardHint: t(TODO_KEYS.KEYBOARD_HINT),
    loading: t(TODO_KEYS.LOADING),
    loadFailed: t(TODO_KEYS.LOAD_FAILED),
  };
}
